import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "../../utils/axiosInstance";
import {
    Warning,
    CheckCircle,
    GasPump,
    UserMinus,
    UserPlus,
    Scales,
    CircleNotch,
    ArrowsCounterClockwise,
    Users,
    X,
    Plus,
} from "@phosphor-icons/react";

const API = `${window.location.origin}/api`;
const MONTHS_PL = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];
const fmtPLN = (n) => Number(n || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export default function PaliwoReconcileView() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [tolerancePln, setTolerancePln] = useState(0.5);
    const [toleranceDays, setToleranceDays] = useState(2);
    const [pojazdTyp, setPojazdTyp] = useState("osobowy");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [openEmp, setOpenEmp] = useState({});  // employee_id -> bool
    const [employees, setEmployees] = useState([]);  // all employees for filter
    const [selectedEmpIds, setSelectedEmpIds] = useState(new Set());  // empty = all
    const [showEmpPicker, setShowEmpPicker] = useState(false);
    // Add-draft-from-orlen modal state: {orlen: {...}, employee_id, employee_name}
    const [addDraft, setAddDraft] = useState(null);

    // Load employees once for the multi-select
    useEffect(() => {
        axios.get(`${API}/employees`).then((r) => {
            setEmployees(r.data || []);
        }).catch(() => {});
    }, []);

    const fetchReconcile = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/tankowania/reconcile`, {
                params: { year, month, tolerance_pln: tolerancePln, tolerance_days: toleranceDays, pojazd_typ: pojazdTyp },
            });
            setData(r.data);
        } catch (e) {
            alert("Błąd: " + (e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }, [year, month, tolerancePln, toleranceDays, pojazdTyp]);

    useEffect(() => { fetchReconcile(); }, [fetchReconcile]);

    const yearOptions = [];
    for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

    // Filter employees from response based on selectedEmpIds
    const filteredEmployees = useMemo(() => {
        if (!data?.employees) return [];
        if (selectedEmpIds.size === 0) return data.employees;
        return data.employees.filter((e) => selectedEmpIds.has(e.employee_id));
    }, [data, selectedEmpIds]);

    // Global summary recomputed from filtered list
    const total = filteredEmployees.reduce((acc, e) => {
        acc.matched += e.summary.matched_count;
        acc.mismatch += e.summary.mismatch_count;
        acc.orlen_only += e.summary.orlen_only_count;
        acc.drafts_only += e.summary.drafts_only_count;
        acc.orlen_total += e.summary.orlen_total;
        acc.drafts_total += e.summary.drafts_total;
        return acc;
    }, { matched: 0, mismatch: 0, orlen_only: 0, drafts_only: 0, orlen_total: 0, drafts_total: 0 });

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="bg-muted border border-amber-200 rounded-xl shadow-sm p-4 sm:p-5">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex items-center gap-2 text-amber-800 font-bold mr-2">
                        <Scales className="w-5 h-5" weight="duotone" />
                        Porównanie z Orlen
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase font-semibold text-slate-500 mb-1">Miesiąc</label>
                        <select
                            value={month}
                            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                            className="px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                            data-testid="reconcile-month"
                        >
                            {MONTHS_PL.map((m, i) => (
                                <option key={i + 1} value={i + 1}>{m}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase font-semibold text-slate-500 mb-1">Rok</label>
                        <select
                            value={year}
                            onChange={(e) => setYear(parseInt(e.target.value, 10))}
                            className="px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                            data-testid="reconcile-year"
                        >
                            {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase font-semibold text-slate-500 mb-1">Typ pojazdu</label>
                        <div className="inline-flex rounded border border-slate-300 overflow-hidden text-sm bg-white">
                            {[
                                { v: "osobowy", l: "Osobowe", title: "Tylko pojazdy osobowe (pracownicy je wpisują)" },
                                { v: "firmowy", l: "Firmowe", title: "Tylko pojazdy firmowe" },
                                { v: "all", l: "Wszystkie", title: "Bez filtra typu pojazdu" },
                            ].map((opt, idx) => (
                                <button
                                    key={opt.v}
                                    onClick={() => setPojazdTyp(opt.v)}
                                    title={opt.title}
                                    className={`px-3 py-2 font-semibold ${pojazdTyp === opt.v ? "bg-amber-600 text-white" : "text-slate-600 hover:bg-slate-50"} ${idx > 0 ? "border-l border-slate-300" : ""}`}
                                    data-testid={`reconcile-typ-${opt.v}`}
                                >
                                    {opt.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase font-semibold text-slate-500 mb-1">Tolerancja kwoty (zł)</label>
                        <input
                            type="number"
                            step="0.1"
                            value={tolerancePln}
                            onChange={(e) => setTolerancePln(parseFloat(e.target.value) || 0)}
                            className="px-3 py-2 border border-slate-300 rounded text-sm bg-white w-28"
                            data-testid="reconcile-tol-pln"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase font-semibold text-slate-500 mb-1">Tolerancja dni</label>
                        <input
                            type="number"
                            min="0"
                            step="1"
                            value={toleranceDays}
                            onChange={(e) => setToleranceDays(parseInt(e.target.value, 10) || 0)}
                            className="px-3 py-2 border border-slate-300 rounded text-sm bg-white w-24"
                            data-testid="reconcile-tol-days"
                        />
                    </div>
                    <button
                        onClick={fetchReconcile}
                        disabled={loading}
                        className="px-3 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded text-sm font-semibold flex items-center gap-1.5"
                        data-testid="reconcile-refresh"
                    >
                        {loading
                            ? <CircleNotch className="w-4 h-4 animate-spin" />
                            : <ArrowsCounterClockwise className="w-4 h-4" />}
                        Porównaj
                    </button>
                    <button
                        onClick={() => setShowEmpPicker(true)}
                        className="px-3 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded text-sm font-semibold flex items-center gap-1.5"
                        data-testid="reconcile-emp-picker-open"
                    >
                        <Users className="w-4 h-4" />
                        {selectedEmpIds.size === 0
                            ? "Wszyscy pracownicy"
                            : `Wybrano: ${selectedEmpIds.size}`}
                    </button>
                    {selectedEmpIds.size > 0 && (
                        <button
                            onClick={() => setSelectedEmpIds(new Set())}
                            className="px-2 py-2 text-rose-600 hover:text-rose-700 text-xs"
                            title="Wyczyść wybór"
                            data-testid="reconcile-emp-clear"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
                <div className="text-xs text-amber-700 mt-2">
                    Porównanie Orlen (wkładka z faktury) z wpisami pracowników (Tankowania pracowników).
                    Dopasowanie po pracowniku (przypisanym do pojazdu w Orlen) + dacie ±{toleranceDays}d.
                    Rozbieżność kwoty &gt; {tolerancePln} zł oznaczona jako problem.
                </div>
            </div>            {/* Global summary */}
            {data && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600" weight="fill" /> Dopasowane
                        </div>
                        <div className="text-2xl font-bold text-emerald-700 mt-1 tabular-nums" data-testid="reconcile-matched-count">
                            {total.matched}
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1">
                            <Warning className="w-3.5 h-3.5 text-amber-600" weight="fill" /> Różne kwoty
                        </div>
                        <div className="text-2xl font-bold text-amber-700 mt-1 tabular-nums" data-testid="reconcile-mismatch-count">
                            {total.mismatch}
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1">
                            <UserMinus className="w-3.5 h-3.5 text-rose-600" weight="fill" /> Brak wpisu pracownika
                        </div>
                        <div className="text-2xl font-bold text-rose-700 mt-1 tabular-nums" data-testid="reconcile-orlen-only-count">
                            {total.orlen_only}
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1">
                            <UserPlus className="w-3.5 h-3.5 text-violet-600" weight="fill" /> Brak w Orlen
                        </div>
                        <div className="text-2xl font-bold text-violet-700 mt-1 tabular-nums" data-testid="reconcile-drafts-only-count">
                            {total.drafts_only}
                        </div>
                    </div>
                </div>
            )}

            {/* Orphan Orlen records (no employee assigned to vehicle) */}
            {data?.orlen_without_employee?.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                    <div className="font-bold text-rose-800 text-sm flex items-center gap-1.5 mb-2">
                        <Warning weight="fill" /> Tankowania Orlen bez przypisanego pracownika
                        ({data.orlen_without_employee.length})
                    </div>
                    <div className="text-xs text-rose-700 mb-2">
                        Te tankowania są w Orlen, ale pojazd nie ma przypisanego pracownika — sverka niemożliwa. Przypisz pracownika w zakładce <strong>Pojazdy</strong>.
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                        {data.orlen_without_employee.map((o, i) => (
                            <div key={i} className="text-xs flex items-center justify-between bg-white border border-rose-200 rounded px-2 py-1">
                                <span>{o.data} · {o.pojazd_nazwa}</span>
                                <span className="font-mono font-bold">{fmtPLN(o.kwota)} zł</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Employees list */}
            {loading && !data ? (
                <div className="py-12 text-center text-slate-400">
                    <CircleNotch className="w-6 h-6 animate-spin inline-block" />
                </div>
            ) : filteredEmployees.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
                    {selectedEmpIds.size > 0
                        ? "Wybrani pracownicy nie mają danych do porównania w tym miesiącu."
                        : "Brak danych do porównania w wybranym miesiącu."}
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredEmployees.map((e) => {
                        const issues = e.summary.mismatch_count + e.summary.orlen_only_count + e.summary.drafts_only_count;
                        const isOpen = openEmp[e.employee_id] || false;
                        const allClean = issues === 0;
                        return (
                            <div
                                key={e.employee_id}
                                className={`bg-white border ${allClean ? "border-emerald-200" : "border-amber-300"} rounded-xl shadow-sm overflow-hidden`}
                                data-testid={`reconcile-emp-${e.employee_id}`}
                            >
                                {/* Header (collapsible) */}
                                <button
                                    onClick={() => setOpenEmp({ ...openEmp, [e.employee_id]: !isOpen })}
                                    className="w-full px-4 sm:px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50"
                                    data-testid={`reconcile-emp-toggle-${e.employee_id}`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        {allClean
                                            ? <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" weight="fill" />
                                            : <Warning className="w-5 h-5 text-amber-600 shrink-0" weight="fill" />}
                                        <div className="text-left min-w-0">
                                            <div className="font-bold text-slate-800 truncate">
                                                {e.employee_full_name || e.employee_name || e.employee_id}
                                                <span className="ml-2 text-xs text-slate-500 font-normal">({e.employee_name})</span>
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                Orlen: <span className="font-mono">{fmtPLN(e.summary.orlen_total)} zł</span>
                                                {" · "}
                                                Pracownik: <span className="font-mono">{fmtPLN(e.summary.drafts_total)} zł</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 shrink-0">
                                        {e.summary.matched_count > 0 && (
                                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[11px] font-semibold">
                                                ✓ {e.summary.matched_count}
                                            </span>
                                        )}
                                        {e.summary.mismatch_count > 0 && (
                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[11px] font-semibold">
                                                ≠ {e.summary.mismatch_count}
                                            </span>
                                        )}
                                        {e.summary.orlen_only_count > 0 && (
                                            <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[11px] font-semibold">
                                                braknej {e.summary.orlen_only_count}
                                            </span>
                                        )}
                                        {e.summary.drafts_only_count > 0 && (
                                            <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full text-[11px] font-semibold">
                                                + {e.summary.drafts_only_count}
                                            </span>
                                        )}
                                    </div>
                                </button>

                                {/* Detail (open) */}
                                {isOpen && (
                                    <div className="border-t border-slate-200 p-3 sm:p-4 space-y-3">
                                        {/* Matched table */}
                                        <ReconcileSection
                                            title="Dopasowane prawidłowo"
                                            icon={<CheckCircle className="w-4 h-4 text-emerald-600" weight="fill" />}
                                            items={e.matched}
                                            type="match"
                                        />
                                        {/* Amount mismatch */}
                                        <ReconcileSection
                                            title="Rozbieżne kwoty"
                                            icon={<Warning className="w-4 h-4 text-amber-600" weight="fill" />}
                                            items={e.amount_mismatch}
                                            type="mismatch"
                                        />
                                        {/* Orlen only */}
                                        <ReconcileSection
                                            title="W Orlen, brak wpisu pracownika"
                                            icon={<UserMinus className="w-4 h-4 text-rose-600" weight="fill" />}
                                            items={e.orlen_only.map((o) => ({ orlen: o }))}
                                            type="orlen_only"
                                            onAddDraft={(orlen) => setAddDraft({
                                                orlen,
                                                employee_id: e.employee_id,
                                                employee_name: e.employee_full_name || e.employee_name,
                                            })}
                                        />
                                        {/* Drafts only */}
                                        <ReconcileSection
                                            title="Wpis pracownika bez tankowania Orlen"
                                            icon={<UserPlus className="w-4 h-4 text-violet-600" weight="fill" />}
                                            items={e.drafts_only.map((d) => ({ draft: d }))}
                                            type="drafts_only"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Employee picker modal */}
            {showEmpPicker && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl border border-border max-w-lg w-full max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <Users className="w-5 h-5 text-amber-600" weight="duotone" />
                                Wybierz pracowników do porównania
                            </h3>
                            <button
                                onClick={() => setShowEmpPicker(false)}
                                className="p-1 hover:bg-slate-100 rounded"
                                data-testid="reconcile-emp-picker-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap gap-2 items-center">
                            <button
                                onClick={() => {
                                    const allIds = employees.map((e) => e._id);
                                    setSelectedEmpIds(new Set(allIds));
                                }}
                                className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded font-semibold"
                                data-testid="reconcile-emp-select-all"
                            >
                                Zaznacz wszystkich
                            </button>
                            <button
                                onClick={() => setSelectedEmpIds(new Set())}
                                className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded font-semibold"
                                data-testid="reconcile-emp-select-none"
                            >
                                Odznacz wszystkich
                            </button>
                            <div className="text-xs text-slate-500 ml-auto">
                                Wybrano: <strong>{selectedEmpIds.size}</strong> / {employees.length}
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2">
                            {employees.map((emp) => {
                                const isSelected = selectedEmpIds.has(emp._id);
                                return (
                                    <label
                                        key={emp._id}
                                        className={`flex items-center gap-3 px-3 py-2 rounded cursor-pointer hover:bg-slate-50 ${isSelected ? "bg-amber-50" : ""}`}
                                        data-testid={`reconcile-emp-item-${emp._id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {
                                                const next = new Set(selectedEmpIds);
                                                if (isSelected) next.delete(emp._id);
                                                else next.add(emp._id);
                                                setSelectedEmpIds(next);
                                            }}
                                            className="w-4 h-4 text-amber-600"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm text-slate-800 truncate">
                                                {emp.imie_nazwisko || emp.name}
                                                <span className="ml-2 text-xs text-slate-500 font-normal">({emp.name})</span>
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                            {employees.length === 0 && (
                                <div className="text-center text-slate-400 py-6 text-sm">
                                    Brak pracowników.
                                </div>
                            )}
                        </div>
                        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
                            <button
                                onClick={() => setShowEmpPicker(false)}
                                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-semibold"
                                data-testid="reconcile-emp-picker-apply"
                            >
                                Zastosuj
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add-draft-from-Orlen modal */}
            {addDraft && (
                <AddDraftModal
                    payload={addDraft}
                    defaultYear={year}
                    defaultMonth={month}
                    onClose={() => setAddDraft(null)}
                    onSuccess={() => { setAddDraft(null); fetchReconcile(); }}
                />
            )}
        </div>
    );
}

function AddDraftModal({ payload, defaultYear, defaultMonth, onClose, onSuccess }) {
    const { orlen, employee_id, employee_name } = payload;
    // Derive default period from Orlen date (day <=15 → 01-15 else 16-30)
    const initialPeriod = useMemo(() => {
        try {
            const parts = String(orlen.data || "").split(".");
            const day = parts.length === 3 ? parseInt(parts[0], 10) : new Date(orlen.data).getUTCDate();
            return day <= 15 ? "01-15" : "16-30";
        } catch { return "01-15"; }
    }, [orlen.data]);
    const [targetYear, setTargetYear] = useState(defaultYear);
    const [targetMonth, setTargetMonth] = useState(defaultMonth);
    const [targetPeriod, setTargetPeriod] = useState(initialPeriod);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    // Convert orlen.data (DD.MM.YYYY) → YYYY-MM-DD for backend
    const isoDate = useMemo(() => {
        const parts = String(orlen.data || "").split(".");
        if (parts.length === 3) {
            const [d, m, y] = parts;
            return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
        }
        return orlen.data;
    }, [orlen.data]);

    const submit = async () => {
        setSaving(true);
        setErr(null);
        try {
            await axios.post(`${API}/employee-paliwo/admin-create`, {
                employee_id,
                data: isoDate,
                kwota: Number(orlen.kwota || 0),
                target_year: parseInt(targetYear, 10),
                target_month: parseInt(targetMonth, 10),
                target_period: targetPeriod,
                orlen_id: orlen.id || orlen._id || null,
                note: `Dodano z porównania Orlen · ${orlen.pojazd_nazwa || ""}`,
            });
            onSuccess();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="add-draft-modal-overlay"
        >
            <div
                className="bg-white rounded-xl border border-border max-w-md w-full flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="add-draft-modal"
            >
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Plus className="w-5 h-5 text-emerald-600" weight="bold" />
                        Dodaj wpis paliwo z Orlen
                    </h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="add-draft-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                        <div className="flex justify-between mb-1">
                            <span className="text-slate-500">Pracownik:</span>
                            <span className="font-semibold">{employee_name}</span>
                        </div>
                        <div className="flex justify-between mb-1">
                            <span className="text-slate-500">Pojazd:</span>
                            <span className="font-mono">{orlen.pojazd_nazwa || "—"}</span>
                        </div>
                        <div className="flex justify-between mb-1">
                            <span className="text-slate-500">Data Orlen:</span>
                            <span className="font-mono">{orlen.data}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Kwota:</span>
                            <span className="font-mono font-bold">
                                {Number(orlen.kwota || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                            </span>
                        </div>
                    </div>
                    <div className="text-xs text-slate-600">
                        Wybierz okres wypłaty, z którego kwota zostanie potrącona:
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-1">
                            <label className="block text-[10px] uppercase font-semibold text-slate-500 mb-1">Rok</label>
                            <select
                                value={targetYear}
                                onChange={(e) => setTargetYear(e.target.value)}
                                className="w-full px-2 py-2 border border-slate-300 rounded text-sm bg-white"
                                data-testid="add-draft-year"
                            >
                                {[defaultYear - 1, defaultYear, defaultYear + 1].map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-span-1">
                            <label className="block text-[10px] uppercase font-semibold text-slate-500 mb-1">Miesiąc</label>
                            <select
                                value={targetMonth}
                                onChange={(e) => setTargetMonth(e.target.value)}
                                className="w-full px-2 py-2 border border-slate-300 rounded text-sm bg-white"
                                data-testid="add-draft-month"
                            >
                                {MONTHS_PL.map((m, i) => (
                                    <option key={i + 1} value={i + 1}>{m}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-span-1">
                            <label className="block text-[10px] uppercase font-semibold text-slate-500 mb-1">Połowa</label>
                            <select
                                value={targetPeriod}
                                onChange={(e) => setTargetPeriod(e.target.value)}
                                className="w-full px-2 py-2 border border-slate-300 rounded text-sm bg-white"
                                data-testid="add-draft-period"
                            >
                                <option value="01-15">01—15</option>
                                <option value="16-30">16—30</option>
                            </select>
                        </div>
                    </div>
                    {err && (
                        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                            {err}
                        </div>
                    )}
                </div>
                <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded"
                        data-testid="add-draft-cancel"
                    >
                        Anuluj
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm font-semibold flex items-center gap-1.5"
                        data-testid="add-draft-submit"
                    >
                        {saving && <CircleNotch className="w-4 h-4 animate-spin" />}
                        Dodaj wpis
                    </button>
                </div>
            </div>
        </div>
    );
}

function ReconcileSection({ title, icon, items, type, onAddDraft }) {
    if (!items || items.length === 0) return null;
    const showAddCol = type === "orlen_only" && typeof onAddDraft === "function";
    return (
        <div>
            <div className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                {icon} {title} ({items.length})
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[480px]">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="text-left py-1.5 px-2 font-semibold text-slate-500">Orlen — data</th>
                            <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Orlen — kwota</th>
                            <th className="text-left py-1.5 px-2 font-semibold text-slate-500">Pracownik — data</th>
                            <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Pracownik — kwota</th>
                            <th className="text-right py-1.5 px-2 font-semibold text-slate-500">Δ</th>
                            {showAddCol && <th className="text-center py-1.5 px-2 font-semibold text-slate-500">Akcja</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, i) => {
                            const o = it.orlen;
                            const d = it.draft;
                            const oAmt = Number(o?.kwota || 0);
                            const dAmt = Number(d?.kwota || 0);
                            const diff = (d && o) ? (dAmt - oAmt) : null;
                            const rowCls = type === "match"
                                ? "bg-emerald-50/30"
                                : type === "mismatch"
                                    ? "bg-amber-50/40"
                                    : type === "orlen_only"
                                        ? "bg-rose-50/40"
                                        : "bg-violet-50/40";
                            return (
                                <tr key={i} className={`border-t border-slate-100 ${rowCls}`}>
                                    <td className="py-1.5 px-2 font-mono text-slate-700">{o?.data || "—"}</td>
                                    <td className="py-1.5 px-2 text-right tabular-nums font-mono">
                                        {o ? `${fmtPLN(oAmt)} zł` : "—"}
                                    </td>
                                    <td className="py-1.5 px-2 font-mono text-slate-700">{d?.data || "—"}</td>
                                    <td className="py-1.5 px-2 text-right tabular-nums font-mono">
                                        {d ? `${fmtPLN(dAmt)} zł` : "—"}
                                    </td>
                                    <td className={`py-1.5 px-2 text-right tabular-nums font-mono font-bold ${diff === null ? "text-slate-400" : Math.abs(diff) < 0.01 ? "text-emerald-700" : diff > 0 ? "text-amber-700" : "text-rose-700"}`}>
                                        {diff === null ? "—" : (diff > 0 ? `+${fmtPLN(diff)}` : fmtPLN(diff))}
                                    </td>
                                    {showAddCol && (
                                        <td className="py-1.5 px-2 text-center">
                                            <button
                                                onClick={() => onAddDraft(o)}
                                                className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-semibold"
                                                title="Dodaj ten wpis jako paliwo pracownika"
                                                data-testid={`add-draft-btn-${i}`}
                                            >
                                                <Plus className="w-3 h-3" weight="bold" />
                                                Dodaj
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
